import { isPlatformBrowser, ViewportScroller } from '@angular/common';
import { provideHttpClient } from '@angular/common/http';
import type { ApplicationConfig } from '@angular/core';
import {
  inject,
  PLATFORM_ID,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { provideClientHydration, withEventReplay } from '@angular/platform-browser';
import {
  NavigationEnd,
  provideRouter,
  Router,
  withComponentInputBinding,
  withInMemoryScrolling,
  withRouterConfig,
  withViewTransitions,
} from '@angular/router';

import { DeviceDetectorService } from 'ngx-device-detector';
import { filter, take } from 'rxjs';

import { UniversalDeviceDetectorService } from '@core/services';
import { BreakpointService } from '@shared/services';

import { routes } from './app.routes';

const initScrollBehavior = () => {
  const platformId = inject(PLATFORM_ID);

  if (!isPlatformBrowser(platformId)) {
    return;
  }

  const viewportScroller = inject(ViewportScroller);
  const router = inject(Router);
  const breakpoints = inject(BreakpointService);

  // Header height compensation: 75 on mobile, 100 on desktop.
  viewportScroller.setOffset(() => (breakpoints.isMobile() ? [0, 75] : [0, 100]));

  // On the first load with a #fragment the browser does a native jump that ignores the header
  // offset (and reload restoration can skip it entirely). Re-run an offset-aware scroll once the
  // initial view has rendered so the target lands just below the fixed header.
  router.events
    .pipe(
      filter((event) => event instanceof NavigationEnd),
      take(1),
    )
    .subscribe(() => {
      const fragment = decodeURIComponent(window.location.hash.replace(/^#/, ''));

      if (fragment) {
        requestAnimationFrame(() => viewportScroller.scrollToAnchor(fragment));
      }
    });
};

export const appConfig: ApplicationConfig = {
  providers: [
    {
      provide: DeviceDetectorService,
      useClass: UniversalDeviceDetectorService,
    },
    provideBrowserGlobalErrorListeners(),
    provideRouter(
      routes,
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled', anchorScrolling: 'enabled' }),
      withRouterConfig({ paramsInheritanceStrategy: 'always' }),
      withViewTransitions(),
      withComponentInputBinding(),
    ),
    provideClientHydration(withEventReplay()),
    provideHttpClient(),
    provideAppInitializer(() => {
      initScrollBehavior();
    }),
  ],
};
